import { createRouter, createWebHistory } from 'vue-router'
import { RouteName } from './routeNames'
import {
  buildSupportedBrowserRouteGuard,
  onSupportedBrowserRouteError
} from './supportedClientGuard'
import { routesDebug } from './routesDebug'
import { getEnv } from '@/libs/env'
import RedirectView from '@/views/RedirectView.vue'
import type { DeviceApi } from '@/libs/deviceApi/deviceApi'
import { buildSdkRedirectsRouteGuard } from './sdkRedirectsGuard'
import { buildRewardsOnlyOnSdkRouteGuard } from './rewardsOnlyOnSdkGuard'

const debugViewEnabled = getEnv('debugViewEnabled')

export const routerFactory = (deviceApi: DeviceApi) => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
      {
        path: '/',
        component: () => import('../layouts/WithNavBarLayout.vue'),
        children: [
          {
            path: '/',
            name: RouteName.Home,
            redirect: { name: RouteName.Missions }
          },
          {
            path: '/logout',
            name: RouteName.Logout,
            component: () => import('../views/LogoutView.vue')
          }
        ]
      },
      {
        path: '/',
        component: () => import('../layouts/FullscreenLayout.vue'),
        children: [
          {
            path: '/update-required',
            name: RouteName.UpdateRequired,
            component: () => import('../views/UpdateRequiredView.vue')
          },
          {
            path: '/maintenance',
            name: RouteName.Maintenance,
            component: () => import('../views/RewardsMaintenance.vue')
          },
          ...(debugViewEnabled ? routesDebug : [])
        ]
      },
      {
        path: '/',
        component: () => import('../layouts/WithFooterLayout.vue'),
        children: [
          {
            path: '/missions',
            name: RouteName.Missions,
            component: () => import('../views/MissionsView.vue')
          },
          {
            path: '/faq',
            name: RouteName.FAQ,
            component: () => import('../views/FAQView.vue')
          },
          {
            path: '/terms',
            name: RouteName.TermsOfUse,
            component: () => import('../views/TermsOfUseView.vue')
          },
          {
            path: '/feedback',
            name: RouteName.Feedback,
            component: () => import('../views/feedback/FeedbackView.vue')
          },
          {
            path: '/feedback-submitted',
            name: RouteName.FeedbackSubmitted,
            component: () => import('../views/feedback/FeedbackSubmittedView.vue')
          },
          {
            path: '/error/client-unsupported',
            name: RouteName.ErrorClientUnsupported,
            component: () => import('../views/ErrorClientUnsupportedView.vue')
          },
          {
            path: '/forms/:slug',
            name: RouteName.CustomForm,
            component: () => import('../views/custom-forms/CustomFormView.vue'),
            props: true
          },
          {
            path: '/debug-info',
            name: RouteName.DebugInfo,
            component: () => import('../views/DebugInfoView.vue')
          },
          {
            path: '/:pathMatch(.*)*',
            name: RouteName.NotFound,
            component: () => import('../views/NotFoundView.vue')
          }
        ]
      },
      {
        path: '/redirect',
        name: RouteName.Redirect,
        component: RedirectView
      }
    ]
  })

  switch (deviceApi.type) {
    case 'sdk':
      router.beforeResolve(buildSdkRedirectsRouteGuard(deviceApi))
      break
    default:
      router.beforeEach(buildRewardsOnlyOnSdkRouteGuard(deviceApi))
      router.beforeEach(buildSupportedBrowserRouteGuard(deviceApi))

      router.onError(onSupportedBrowserRouteError(router))
  }

  return router
}
